import React, { useEffect } from "react";
import UserScreenWindow from "../Components/UserScreenWindow";
import { useNavigate } from "react-router-dom";
import { setDocData, getDocById } from "../../../utils/firebase/Firebase.utils";
import { v4 } from "uuid";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import { dualNotification } from "../../../utils/EleghoUtils";
import {
  FaFileExport,
  FaHome,
  FaRegFolderOpen,
  FaCalendarAlt,
  FaClock,
  FaList,
  FaTruck,
  FaPaperPlane,
} from "react-icons/fa";
import { useCompany } from "../../../context/CompanyContext";
import InputBox from "../../Dashboard/Components/InputBox";
import { adminEmailAddress } from "../../../utils/constants";

function Gatepass() {
  const [inProcess, setInProcess] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);
  const [formData, setFormData] = React.useState({});
  const [assignedId, setAssignedId] = React.useState(
    v4().slice(0, 8).toUpperCase()
  );
  const [linkedIdentifiers, setLinkedIdentifiers] = React.useState([]);
  const navigate = useNavigate();
  const currentUser = useSelector((state) => state.userObject);

  const { getCapitalizedTerm } = useCompany();

  useEffect(() => {
    const fetchIdentifiers = async () => {
      const user = await getDocById("users", currentUser.uid);
      setLinkedIdentifiers(user.linkedIdentifiers);
      setIsLoading(false);
    };
    fetchIdentifiers();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  if (isLoading) {
    return (
      <UserScreenWindow label="Gate Pass">
        <div className="flex justify-center items-center h-screen">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#363062]"></div>
        </div>
      </UserScreenWindow>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setInProcess(true);

    if (
      !formData.identifier ||
      !formData.deliveryDate ||
      !formData.deliveryTime ||
      !formData.items
    ) {
      toast.error("All fields are required");
      setInProcess(false);
      return;
    }

    const docObject = {
      ...formData,
      user: currentUser.email,
      requestedBy: currentUser.displayName,
      status: "pending",
      requestDateTime: new Date(),
      requestType: "gatePass",
      id: assignedId,
    };

    await setDocData("gatePass", assignedId, docObject);

    const notificationObject = {
      recipient: adminEmailAddress,
      recipientName: "Admin",
      sender: currentUser.email,
      senderName: currentUser.displayName,
      type: "gatePass",
      subject: "New Gate Pass Request",
      message: `A new gate pass request has been submitted by ${currentUser.displayName} for ${formData.identifier}.`,
      url: `/security?link=gatePass`,
      id: assignedId,
      dateTime: new Date(),
      read: false,
    };

    await dualNotification(notificationObject);

    toast.success("Request submitted successfully");
    setInProcess(false);
    navigate("/gatepass-confirmation/" + assignedId);
  };

  const gatePassTypes = ["Move In", "Move Out", "Delivery", "Pull Out"];

  return (
    <UserScreenWindow label="Gate Pass">
      <div className="w-full mx-auto px-3 pb-20">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 bg-[#363062]/5 p-4 rounded-lg">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-[#363062] rounded-lg">
              <FaFileExport className="text-white text-base" />
            </div>
            <div>
              <h1 className="text-sm font-medium text-[#363062]">
                Gate Pass Application
              </h1>
              <p className="text-xs text-gray-500">
                Submit your gate pass request for approval
              </p>
              <p className="text-xs text-gray-500 mt-0.5">Ref: {assignedId}</p>
            </div>
          </div>
        </div>

        {/* Form Fields */}
        <div className="space-y-4 mt-4 bg-white shadow-md rounded-lg p-4">
          {/* Lot Number */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-[#363062] flex items-center gap-1.5">
              <FaHome className="text-xs" />
              {getCapitalizedTerm("identifier")}
            </label>
            {currentUser.roles.includes("user") ? (
              <select
                className="w-full rounded-md bg-gray-50 border border-gray-200 p-2 text-xs focus:ring-1 focus:ring-[#363062] focus:border-[#363062]"
                name="identifier"
                onChange={handleChange}
              >
                <option value="">{`Select ${getCapitalizedTerm(
                  "identifier"
                )}`}</option>
                {linkedIdentifiers.map((identifier, index) => (
                  <option key={index} value={identifier}>
                    {identifier}
                  </option>
                ))}
              </select>
            ) : (
              <InputBox
                name="identifier"
                value={formData.identifier}
                onChange={handleChange}
                placeholder={`Enter ${getCapitalizedTerm("identifier")}`}
              />
            )}
          </div>

          {/* Type of Gate Pass */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-[#363062] flex items-center gap-1.5">
              <FaRegFolderOpen className="text-xs" />
              Type of Gate Pass
            </label>
            <select
              className="w-full rounded-md bg-gray-50 border border-gray-200 p-2 text-xs focus:ring-1 focus:ring-[#363062] focus:border-[#363062]"
              name="gatePassType"
              onChange={handleChange}
            >
              <option value="">Select Type of Gate Pass</option>
              {gatePassTypes.map((type, index) => (
                <option key={index} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>

          {/* Date and Time */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-[#363062] flex items-center gap-1.5">
                <FaCalendarAlt className="text-xs" />
                Date
              </label>
              <input
                type="date"
                className="w-full rounded-md bg-gray-50 border border-gray-200 p-2 text-xs focus:ring-1 focus:ring-[#363062] focus:border-[#363062]"
                name="deliveryDate"
                onChange={handleChange}
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-[#363062] flex items-center gap-1.5">
                <FaClock className="text-xs" />
                Estimated Time
              </label>
              <input
                type="time"
                className="w-full rounded-md bg-gray-50 border border-gray-200 p-2 text-xs focus:ring-1 focus:ring-[#363062] focus:border-[#363062]"
                name="deliveryTime"
                onChange={handleChange}
              />
            </div>
          </div>

          {/* Items List */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-[#363062] flex items-center gap-1.5">
              <FaList className="text-xs" />
              List of Items
            </label>
            <textarea
              className="w-full rounded-md bg-gray-50 border border-gray-200 p-2.5 text-xs focus:ring-1 focus:ring-[#363062] focus:border-[#363062]"
              rows={4}
              placeholder="e.g. 1. Refrigerator - 1 Unit, 2. Sofa Bed - 2 Units"
              name="items"
              onChange={handleChange}
            />
          </div>

          {/* Remarks */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-[#363062] flex items-center gap-1.5">
              <FaList className="text-xs" />
              Remarks (Optional)
            </label>
            <textarea
              className="w-full rounded-md bg-gray-50 border border-gray-200 p-2.5 text-xs focus:ring-1 focus:ring-[#363062] focus:border-[#363062]"
              rows={4}
              placeholder="Additional notes or special instructions"
              name="remarks"
              onChange={handleChange}
            />
          </div>

          {/* Delivery Company */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-[#363062] flex items-center gap-1.5">
              <FaTruck className="text-xs" />
              Delivery Company (Optional)
            </label>
            <input
              type="text"
              className="w-full rounded-md bg-gray-50 border border-gray-200 p-2 text-xs focus:ring-1 focus:ring-[#363062] focus:border-[#363062]"
              placeholder="e.g. Crate and Barrel"
              name="deliveryCompany"
              onChange={handleChange}
            />
          </div>
        </div>

        {/* Bottom Navigation */}
        <div className="mt-4">
          <div className="max-w-md mx-auto">
            <button
              onClick={handleSubmit}
              disabled={inProcess}
              className="w-full bg-[#363062] text-white py-3 rounded-md text-xs font-medium shadow-lg flex items-center justify-center gap-2 hover:bg-[#2d2850] transition-colors"
            >
              {inProcess ? (
                <>
                  <svg
                    className="animate-spin h-3.5 w-3.5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Processing...
                </>
              ) : (
                <>
                  <FaPaperPlane className="text-xs" />
                  Submit Application
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </UserScreenWindow>
  );
}

export default Gatepass;
